import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';
import { CreateWorkspaceCard } from './CreateWorkspaceCard.js';

type Briefing = Awaited<ReturnType<typeof api.get<'/api/v1/today'>>>;
type Phrase = Briefing['headline'];

/**
 * Today.
 *
 * The screen most people will only ever see, and its promise is the hardest one in the product:
 * silence means handled. Everything here is arranged around making that promise safe to believe.
 *
 * **The problems come first.** A mailbox that stopped syncing and a Safety Vault with no verified
 * snapshot are rendered above the pleasant parts, because a calm screen over a broken mailbox is
 * the worst failure this product can have — it is the interface saying "nothing needs you" while
 * mail arrives that nobody has read.
 *
 * **"All caught up" is the server's word, not a count of empty arrays.** The client never infers
 * it from `attention.length === 0`, because a broken mailbox produces empty arrays honestly.
 *
 * **A failure to load is shown as a failure.** Not an empty state. Rendering a calm screen when the
 * request failed is telling somebody there is nothing to do on the strength of no information.
 */

function greetingKey(hour: number): string {
  if (hour < 12) return 'today.greeting.morning';
  if (hour < 18) return 'today.greeting.afternoon';
  return 'today.greeting.evening';
}

export function TodayScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /*
   * Which mailbox the screen speaks for; null is the whole tenant.
   *
   * Local state rather than a route param, deliberately: the scope is a lens, not a place. The
   * screen a person returns to tomorrow is Today for everything, and a bookmarked filter that
   * silently hides four mailboxes is the "silence by not looking" failure this screen exists
   * to prevent.
   */
  const [scopeMailboxId, setScopeMailboxId] = useState<string | null>(null);

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      setLoading(true);
      try {
        const result = await api.get(
          '/api/v1/today',
          scopeMailboxId === null ? undefined : { mailboxId: scopeMailboxId },
          { tenantId, signal },
        );
        setBriefing(result);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        /*
         * A failed load is never rendered as an empty Today.
         *
         * The empty state says "you are all caught up", and saying it because a request failed is
         * telling somebody there is nothing to do on the strength of no information at all.
         */
        setBriefing(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      } finally {
        setLoading(false);
      }
    },
    [tenantId, scopeMailboxId],
  );

  const respond = useCallback(
    async (eventId: string, response: 'accepted' | 'declined' | 'tentative') => {
      if (tenantId === undefined) return;
      try {
        await api.post(
          '/api/v1/calendar/events/{eventId}/respond',
          { response },
          { eventId },
          { tenantId },
        );
        await load();
      } catch {
        /* The next load shows the truth either way. */
      }
    },
    [tenantId, load],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  if (activeWorkspace === null) {
    // Signing up and landing nowhere is a dead end; the first thing to offer is the one action
    // that unblocks everything else.
    return (
      <>
        <header className="greeting">
          <h1 className="greeting__title">{t('workspace.none.title')}</h1>
          <p className="empty__body">{t('workspace.none.body')}</p>
        </header>
        <CreateWorkspaceCard />
      </>
    );
  }

  if (failure !== null) {
    return (
      <>
        <header className="greeting">
          <h1 className="greeting__title">{t(greetingKey(new Date().getHours()))}</h1>
        </header>
        <Alert tone="critical" title={t('today.error.title')}>
          <p>{t('today.error.body')}</p>
          <Button onClick={() => void load()}>{t('today.retry')}</Button>
        </Alert>
      </>
    );
  }

  if (briefing === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  const phrase = (value: Phrase): string => translator.t(value.key, value.params);
  const problems = [
    ...briefing.mailboxes.filter(
      (mailbox) => mailbox.issue !== null && mailbox.severity !== 'info',
    ),
    ...briefing.vault.filter((entry) => entry.issue !== null && entry.severity !== 'info'),
  ];

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t(greetingKey(new Date().getHours()))}</h1>
        <p className="greeting__headline">{phrase(briefing.headline)}</p>
        <div className="greeting__summary">
          <span>{t('today.attentionCount', { count: briefing.attention.length })}</span>
          <span>{t('today.deadlinesThisWeek', { count: briefing.deadlines.length })}</span>
          <span>{t('today.peopleWaiting', { count: briefing.waitingOnYou.length })}</span>
        </div>
      </header>

      {/*
        Doors, not summaries.

        Each of these has a screen that does the subject justice, and reproducing any of them
        here badly would be worse than a line of text and a link. They appear only when there is
        something behind them: a row of zeroes is chrome, and this screen's whole job is to be
        short enough that somebody trusts it.
      */}
      {(briefing.counts.offersPending > 0 ||
        briefing.counts.moneyTrouble > 0 ||
        briefing.counts.anomalies > 0) && (
        <section className="card">
          <ul className="today-list">
            {briefing.counts.offersPending > 0 && (
              <li className="today-list__item">
                <span className="badge badge--ok">{t('today.doors.offers.badge')}</span>
                <span className="today-list__subject">
                  {t('today.doors.offers', { count: briefing.counts.offersPending })}
                </span>
                <Link to="/decisions" className="link-button">
                  {t('today.doors.offers.action')}
                </Link>
              </li>
            )}
            {briefing.counts.moneyTrouble > 0 && (
              <li className="today-list__item">
                <span className="badge badge--critical">{t('today.doors.money.badge')}</span>
                <span className="today-list__subject">
                  {t('today.doors.money', { count: briefing.counts.moneyTrouble })}
                </span>
                <Link to="/money" className="link-button">
                  {t('today.doors.money.action')}
                </Link>
              </li>
            )}
            {briefing.counts.anomalies > 0 && (
              <li className="today-list__item">
                <span className="badge">{t('today.doors.anomalies.badge')}</span>
                <span className="today-list__subject">
                  {t('today.doors.anomalies', { count: briefing.counts.anomalies })}
                </span>
                <Link to="/activity" className="link-button">
                  {t('today.doors.anomalies.action')}
                </Link>
              </li>
            )}
          </ul>
        </section>
      )}

      {/*
        The mailbox switcher, only when there is something to switch.

        One mailbox means the chips would be a row of chrome restating the page title. The counts
        come from the always-complete health strip, so a mailbox that needs attention is visible
        from every scope — the chip row is the one place the other mailboxes keep a voice while
        the screen speaks for one of them.
      */}
      {briefing.mailboxes.length > 1 && (
        <nav className="mailbox-chips" aria-label={t('today.scope.label')}>
          <button
            type="button"
            className={
              scopeMailboxId === null ? 'mailbox-chip mailbox-chip--active' : 'mailbox-chip'
            }
            aria-pressed={scopeMailboxId === null}
            onClick={() => setScopeMailboxId(null)}
          >
            {t('today.scope.all')}
          </button>
          {briefing.mailboxes.map((mailbox) => (
            <button
              key={mailbox.mailAccountId}
              type="button"
              className={
                scopeMailboxId === mailbox.mailAccountId
                  ? 'mailbox-chip mailbox-chip--active'
                  : 'mailbox-chip'
              }
              aria-pressed={scopeMailboxId === mailbox.mailAccountId}
              onClick={() => setScopeMailboxId(mailbox.mailAccountId)}
            >
              <span
                className={`mailbox-chip__health mailbox-chip__health--${mailbox.severity}`}
                aria-hidden="true"
              />
              {mailbox.emailAddress}
              {mailbox.openAttention > 0 && (
                <span className="mailbox-chip__count">{mailbox.openAttention}</span>
              )}
            </button>
          ))}
        </nav>
      )}

      {/*
        What Butler is working through, while it is working through it.

        Above the pleasant parts but below the problems: a person watching a fresh deployment
        deserves to see the machine move, and a progress bar over real counts is the difference
        between "is it doing anything?" and watching it happen. The section disappears when
        Butler is caught up — the finished state's absence is the message.
      */}
      {briefing.progress !== null && (
        <section className="card" aria-labelledby="progress-heading">
          <h2 className="card__title" id="progress-heading">
            {t('today.progress.title')}
          </h2>
          {briefing.progress.classification !== null && (
            <div className="progress">
              <div className="progress__text">
                <span>
                  {t('today.progress.classifying', {
                    done: briefing.progress.classification.done,
                    total: briefing.progress.classification.total,
                  })}
                </span>
                <span className="progress__pct">
                  {translator.formatNumber(
                    briefing.progress.classification.done /
                      Math.max(1, briefing.progress.classification.total),
                    { style: 'percent' },
                  )}
                </span>
              </div>
              <div
                className="progress__bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={briefing.progress.classification.total}
                aria-valuenow={briefing.progress.classification.done}
              >
                <div
                  className="progress__fill"
                  style={{
                    width: `${
                      (100 * briefing.progress.classification.done) /
                      Math.max(1, briefing.progress.classification.total)
                    }%`,
                  }}
                />
              </div>
              <p className="progress__hint">
                {briefing.progress.classification.lastPassAt === null
                  ? t('today.progress.firstPass')
                  : t('today.progress.lastPass', {
                      count: briefing.progress.classification.lastPassCount,
                      time: translator.formatDate(
                        new Date(briefing.progress.classification.lastPassAt),
                        { hour: '2-digit', minute: '2-digit' },
                      ),
                    })}
              </p>
            </div>
          )}
          {briefing.progress.rescue !== null && (
            <div className="progress">
              <div className="progress__text">
                <span>
                  {t('today.progress.rescue', {
                    done: briefing.progress.rescue.chunksDone,
                    total: briefing.progress.rescue.totalChunks,
                    messages: briefing.progress.rescue.totalMessages,
                  })}
                </span>
              </div>
              <div
                className="progress__bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={briefing.progress.rescue.totalChunks}
                aria-valuenow={briefing.progress.rescue.chunksDone}
              >
                <div
                  className="progress__fill"
                  style={{
                    width: `${
                      (100 * briefing.progress.rescue.chunksDone) /
                      Math.max(1, briefing.progress.rescue.totalChunks)
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
        </section>
      )}

      {/*
        Problems above everything.

        Not a footer, not a settings screen. If a mailbox stopped syncing, that is the most
        important thing on this page and it outranks every message on it.
      */}
      {problems.length > 0 && (
        <Alert tone="critical" title={t('today.section.health')}>
          <ul className="issue-list">
            {problems.map((problem) => (
              <li key={`${problem.mailAccountId}-${problem.issue?.key ?? ''}`}>
                {problem.issue === null ? null : phrase(problem.issue)}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <section className="card" aria-labelledby="attention-heading">
        <h2 className="card__title" id="attention-heading">
          {t('today.section.attention')}
        </h2>
        {briefing.attention.length === 0 ? (
          /*
           * The empty state, and the condition on it.
           *
           * `allClear` comes from the server, which knows whether the lists are empty *and*
           * whether anything is broken. Inferring it here from an empty array would show "you are
           * all caught up" over a mailbox that has not synced in three days.
           */
          <div className="empty">
            <p>
              <strong>
                {briefing.allClear
                  ? t('today.empty.title')
                  : t('today.headline.needsFixing', {
                      count: problems.length,
                    })}
              </strong>
            </p>
            {briefing.allClear && <p className="empty__body">{t('today.empty.body')}</p>}
          </div>
        ) : (
          <ul className="today-list">
            {briefing.attention.map((item) => (
              <li className="today-list__item" key={item.messageId}>
                <span className={`badge badge--${item.band}`}>{item.band}</span>
                <span className="today-list__subject">{item.subject}</span>
                <span className="today-list__meta">{item.from}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {briefing.appointments.length > 0 && (
        <section className="card" aria-labelledby="appointments-heading">
          <h2 className="card__title" id="appointments-heading">
            {t('today.section.appointments')}
          </h2>
          <ul className="today-list">
            {briefing.appointments.map((appointment) => (
              <li className="today-list__item" key={appointment.eventId}>
                <span className="badge">
                  {appointment.allDay
                    ? translator.formatDate(new Date(appointment.startsAt))
                    : translator.formatDate(new Date(appointment.startsAt), {
                        weekday: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                </span>
                <span className="today-list__subject">
                  {appointment.summary === ''
                    ? t('today.appointment.untitled')
                    : appointment.summary}
                </span>
                <span className="today-list__meta">
                  {[
                    appointment.location,
                    appointment.organizer,
                    /* An unanswered invitation is the actionable one, so it says so. */
                    appointment.ownResponse === null
                      ? t('today.appointment.unanswered')
                      : t(`today.appointment.response.${appointment.ownResponse}`),
                  ]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                </span>
                {appointment.ownResponse === null && (
                  <span className="mailbox-form__actions">
                    <Button
                      variant="primary"
                      onClick={() => void respond(appointment.eventId, 'accepted')}
                    >
                      {t('today.appointment.accept')}
                    </Button>
                    <Button onClick={() => void respond(appointment.eventId, 'tentative')}>
                      {t('today.appointment.tentative')}
                    </Button>
                    <Button onClick={() => void respond(appointment.eventId, 'declined')}>
                      {t('today.appointment.decline')}
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {briefing.deadlines.length > 0 && (
        <section className="card" aria-labelledby="deadlines-heading">
          <h2 className="card__title" id="deadlines-heading">
            {t('today.section.deadlines')}
          </h2>
          <ul className="today-list">
            {briefing.deadlines.map((deadline) => (
              <li className="today-list__item" key={deadline.obligationId}>
                <span className={deadline.overdue ? 'badge badge--critical' : 'badge'}>
                  {deadline.overdue
                    ? t('today.deadline.overdue', { count: Math.abs(deadline.daysRemaining) })
                    : deadline.daysRemaining === 0
                      ? t('today.deadline.dueToday')
                      : t('today.deadline.due', { count: deadline.daysRemaining })}
                </span>
                <span className="today-list__subject">
                  {deadline.organisation ?? deadline.kind}
                </span>
                {deadline.amountMinor !== null && deadline.currency !== null && (
                  <span className="today-list__meta">
                    {translator.formatNumber(deadline.amountMinor / 100, {
                      style: 'currency',
                      currency: deadline.currency,
                    })}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(briefing.waitingOnYou.length > 0 || briefing.waitingOnOthers.length > 0) && (
        <section className="card" aria-labelledby="waiting-heading">
          <h2 className="card__title" id="waiting-heading">
            {t('today.section.waitingOnYou')}
          </h2>
          {/*
            Two lists, never merged. "Four people are waiting on you" and "you are waiting on four
            people" are opposite feelings, and one combined count is a number nobody can act on.
          */}
          <WaitingList items={briefing.waitingOnYou} />
          {briefing.waitingOnOthers.length > 0 && (
            <>
              <h3 className="card__subtitle">{t('today.section.waitingOnOthers')}</h3>
              <WaitingList items={briefing.waitingOnOthers} />
            </>
          )}
        </section>
      )}

      <section className="card" aria-labelledby="handled-heading">
        <h2 className="card__title" id="handled-heading">
          {t('today.section.handled')}
        </h2>
        <ul className="today-stats">
          <li>{t('today.handled.filed', { count: briefing.handled.messagesFiled })}</li>
          {briefing.handled.proposalsExecuted > 0 && (
            <li>{t('today.handled.proposals', { count: briefing.handled.proposalsExecuted })}</li>
          )}
          {briefing.handled.unsubscribes > 0 && (
            <li>{t('today.handled.unsubscribes', { count: briefing.handled.unsubscribes })}</li>
          )}
          {/* What can still be taken back is what makes the rest safe to read. */}
          <li className="today-stats__undo">
            {t('today.handled.undoable', { count: briefing.handled.undoableBatches })}
          </li>
        </ul>
      </section>

      <section className="card" aria-labelledby="vault-heading">
        <h2 className="card__title" id="vault-heading">
          {t('today.section.vault')}
        </h2>
        {briefing.vault.length === 0 ? (
          <p className="empty__body">{t('vault.explainer')}</p>
        ) : (
          <ul className="today-list">
            {briefing.vault.map((entry) => (
              <li className="today-list__item" key={entry.mailAccountId}>
                <span className="today-list__subject">{entry.emailAddress}</span>
                <span className="today-list__meta">
                  {entry.issue === null
                    ? entry.verifiedAt === null
                      ? t('today.vault.ok')
                      : t('today.vault.verified', {
                          date: translator.formatDate(new Date(entry.verifiedAt)),
                        })
                    : phrase(entry.issue)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {loading && (
        <p className="today-refreshing" role="status" aria-live="polite">
          {t('common.loading')}
        </p>
      )}
    </>
  );
}

function WaitingList({ items }: { items: Briefing['waitingOnYou'] }): ReactNode {
  const { t } = useI18n();

  return (
    <ul className="today-list">
      {items.map((item) => (
        <li className="today-list__item" key={item.commitmentId}>
          <span className={item.overdue ? 'badge badge--critical' : 'badge'}>
            {t('today.waiting.since', { count: item.daysWaiting })}
          </span>
          <span className="today-list__subject">{item.summary}</span>
          {item.counterparty !== null && (
            <span className="today-list__meta">{item.counterparty}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
